import React from "react";
import {infographicWideWidthFrac, infographicWideTextPx, enterProgress, statCountAt,
  infographicVisibleAt, INFOGRAPHIC_DEFAULT_SIZE, INFOGRAPHIC_DEFAULT_COLOR,
  resolveInfographicSkin, glyphSparks, INFOGRAPHIC_LAYOUT, infographicFontPx,
  infographicTitleBandMetrics, infographicBannerBurstMetrics, type InfographicItem,
} from "../../lib/infographics";
import {INFO_BASE_STACK, type InfoFontStyle} from "../../lib/caption-fonts";

const INFO_FONT = INFO_BASE_STACK;
const BASE_H = 640;
function itemStyle(x: number, y: number): React.CSSProperties {
  return {position: "absolute", left: `${x * 100}%`, top: `${y * 100}%`, transform: "translate(-50%, -50%)", whiteSpace: "nowrap"};
}

/** Original Story DOM. No Remotion hooks; native editor supplies source clock and viewport. */
/**
 * 📐 **풀와이드 기준 폭**(Codex 1R P1-4) — 화면 전폭을 쓰는 장치(bannerBurst·titleBand)는 `360*S` 로
 *   계산하면 안 된다. 9:16 템플릿에서는 S=width/360 이라 360*S=width 로 우연히 맞지만, 16:9
 *   (Overlay169Template)는 S=height/640 이라 360*S = 0.5625×height = **실제 폭의 1/3**이 된다
 *   (실측 595px vs 캔버스 1882px). 캔버스 렌더러는 처음부터 실제 W 를 쓰므로 양쪽 룩이 갈라진다.
 *   그래서 뷰포트 실폭을 prop 으로 받고, 안 넘어오면 종전 근사(360*S)로 폴백한다.
 */
function viewportW(W: number | undefined, S: number): number {
  return typeof W === "number" && W > 0 ? W : 360 * S;
}

export function InfographicNode({ it, tRel, S, W, font }: { it: InfographicItem; tRel: number; S: number; W?: number; font?: InfoFontStyle }) {
  // 🔤 제목 폰트 따라가기 — 미지정=기본 스택·주어진 weight 그대로(픽셀 불변). 커스텀은 폰트 고유 weight(합성볼드 회피).
  const ff = font?.family ?? INFO_FONT;
  const fw = (w: number) => font?.weight ?? w;
  if (!infographicVisibleAt(it, tRel)) return null;
  const p = enterProgress(tRel, it.start ?? 0);
  if (p <= 0) return null;
  const color = it.color ?? INFOGRAPHIC_DEFAULT_COLOR;
  const fs = infographicFontPx(it.sizePct ?? INFOGRAPHIC_DEFAULT_SIZE[it.kind], BASE_H * S);
  const alpha = Math.min(1, Math.max(0, p) * 1.4);

  if (it.kind === "badge") {
    return (
      <div style={{ ...itemStyle(it.x, it.y), opacity: alpha }}>
        <div style={{ transform: `scale(${Math.max(0.01, p)})`, background: color, color: "#fff", fontFamily: ff, fontWeight: fw(900), fontSize: fs, padding: `${fs * 0.38}px ${fs * 0.6}px`, borderRadius: fs * 1.1, boxShadow: `0 ${fs * 0.12}px ${fs * 0.5}px rgba(0,0,0,0.35)` }}>
          {it.text}
        </div>
      </div>
    );
  }
  if (it.kind === "price") {
    const priceText = String(it.value ?? "").trim() || it.text;
    const labelText = String(it.value ?? "").trim() ? it.text : "";
    return (
      <div style={{ ...itemStyle(it.x, it.y), opacity: alpha }}>
        <div style={{ transform: `translateY(${(1 - p) * fs * 1.2}px)`, background: "#fff", borderRadius: fs * 0.35, padding: `${fs * 0.5}px ${fs * 0.7}px`, boxShadow: `0 ${fs * 0.15}px ${fs * 0.6}px rgba(0,0,0,0.3)`, textAlign: "center", fontFamily: ff }}>
          {labelText ? <div style={{ fontWeight: fw(700), fontSize: fs * 0.62, color: "#666", lineHeight: 1.25 }}>{labelText}</div> : null}
          <div style={{ fontWeight: fw(900), fontSize: fs * 1.25, color, lineHeight: 1.2 }}>{priceText}</div>
          {it.sub ? <div style={{ fontWeight: fw(700), fontSize: fs * 0.62, color: "#9a9a9a", textDecoration: "line-through", lineHeight: 1.25 }}>{it.sub}</div> : null}
        </div>
      </div>
    );
  }
  if (it.kind === "stat") {
    const target = typeof it.value === "number" ? it.value : Number(String(it.value ?? "").replace(/[^\d.-]/g, "")) || 0;
    const now = statCountAt(tRel, target, it.start ?? 0);
    return (
      <div style={{ ...itemStyle(it.x, it.y), opacity: alpha, textAlign: "center", fontFamily: ff, transform: `translate(-50%, -50%) scale(${0.9 + 0.1 * p})` }}>
        <div style={{ fontWeight: fw(900), fontSize: fs, color, textShadow: `0 ${fs * 0.08}px ${fs * 0.35}px rgba(0,0,0,0.55)`, lineHeight: 1.1 }}>
          {now.toLocaleString("ko-KR")}{it.sub ?? ""}
        </div>
        <div style={{ fontWeight: fw(800), fontSize: fs * 0.34, color: "#fff", textShadow: `0 0 ${fs * 0.2}px rgba(0,0,0,0.65)`, marginTop: fs * 0.12 }}>{it.text}</div>
      </div>
    );
  }
  if (it.kind === "list") {
    const items = it.items ?? [];
    return (
      <div style={{ ...itemStyle(it.x, it.y), opacity: alpha }}>
        <div style={{ background: "rgba(17,17,17,0.82)", borderRadius: fs * 0.45, padding: `${fs * 0.45}px ${fs * 0.7}px`, boxShadow: `0 ${fs * 0.12}px ${fs * 0.5}px rgba(0,0,0,0.35)`, fontFamily: ff, textAlign: "left" }}>
          {it.text ? <div style={{ fontWeight: fw(900), fontSize: fs, color, lineHeight: 1.5 }}>{it.text}</div> : null}
          {items.map((li, k) => {
            const lp = enterProgress(tRel, (it.start ?? 0) + 0.25 + k * 0.35);
            return (
              <div key={k} style={{ fontWeight: fw(800), fontSize: fs, color: "#fff", lineHeight: 1.5, opacity: Math.min(1, Math.max(0, lp) * 1.4), whiteSpace: "nowrap" }}>
                <span style={{ color }}>✓ </span>{li}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  if (it.kind === "compare") {
    const slide = (1 - p) * fs * 2.2;
    const pill = (bg: string, txt: string, dx: number): React.CSSProperties => ({ background: bg, color: "#fff", fontFamily: ff, fontWeight: fw(900), fontSize: fs, padding: `${fs * 0.38}px ${fs * 0.55}px`, borderRadius: fs * 0.5, transform: `translateX(${dx}px)`, boxShadow: `0 ${fs * 0.1}px ${fs * 0.4}px rgba(0,0,0,0.35)`, whiteSpace: "nowrap" });
    return (
      <div style={{ ...itemStyle(it.x, it.y), opacity: alpha }}>
        <div style={{ display: "flex", alignItems: "center", gap: fs * 0.5 }}>
          <div style={pill("rgba(17,17,17,0.85)", it.text, -slide)}>{it.text}</div>
          <div style={{ fontFamily: ff, fontWeight: fw(900), fontSize: fs * 0.8, color: "#F6C445" }}>VS</div>
          <div style={pill(color, it.sub ?? "", slide)}>{it.sub ?? ""}</div>
        </div>
      </div>
    );
  }
  if (it.kind === "ring") {
    const r = fs; // ring 은 sizePct=반경 비율
    const pulse = p >= 1 ? 1 + 0.04 * Math.sin(tRel * 4.2) : 1;
    const C = 2 * Math.PI * 45;
    return (
      <div style={{ ...itemStyle(it.x, it.y), opacity: alpha }}>
        <svg width={r * 2.4} height={r * 2.4} viewBox="0 0 100 100" style={{ overflow: "visible", transform: `scale(${pulse})` }}>
          <circle cx="50" cy="50" r="45" fill="none" stroke={color} strokeWidth={Math.max(3, 45 * 0.14)} strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={C * (1 - Math.min(1, p))} transform="rotate(-90 50 50)" />
        </svg>
        {it.text ? <div style={{ position: "absolute", left: "50%", top: `calc(100% + ${r * 0.1}px)`, transform: "translateX(-50%)", fontFamily: ff, fontWeight: fw(900), fontSize: r * 0.34, color: "#fff", textShadow: `0 0 ${r * 0.18}px rgba(0,0,0,0.65)`, whiteSpace: "nowrap" }}>{it.text}</div> : null}
      </div>
    );
  }
  if (it.kind === "bar") {
    const target = typeof it.value === "number" ? it.value : 0;
    const now = statCountAt(tRel, target, it.start ?? 0);
    const w = fs * 10, h = fs * 0.85;
    return (
      <div style={{ ...itemStyle(it.x, it.y), opacity: alpha }}>
        <div style={{ width: w, fontFamily: ff }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: fs * 0.25 }}>
            <span style={{ fontWeight: fw(800), fontSize: fs, color: "#fff", textShadow: `0 0 ${fs * 0.25}px rgba(0,0,0,0.6)` }}>{it.text}</span>
            <span style={{ fontWeight: fw(900), fontSize: fs, color: "#fff", textShadow: `0 0 ${fs * 0.25}px rgba(0,0,0,0.6)` }}>{now}%</span>
          </div>
          <div style={{ width: "100%", height: h, borderRadius: h / 2, background: "rgba(255,255,255,0.25)" }}>
            <div style={{ width: `${Math.max(h / w * 100, now)}%`, height: h, borderRadius: h / 2, background: color }} />
          </div>
        </div>
      </div>
    );
  }
  // 📽 P3 스타일 그래픽 오버레이 4종 — 캔버스 drawInfographics 와 같은 스펙·스킨·타이밍(SSOT 공유).
  if (it.kind === "panelCard") {
    const sk = resolveInfographicSkin(it);
    const body = it.items?.length ? it.items : it.sub ? [it.sub] : [];
    // 📐 폭·여백은 양 렌더러 공용 규칙에서만 읽는다(Codex 1R P2-6 — 종전 minWidth fs*11 vs 캔버스 fs*9 괴리).
    //    box-sizing:border-box 로 두어야 minWidth 가 캔버스의 "텍스트 최소폭 + 좌우 여백" 과 같은 총폭이 된다.
    const L = INFOGRAPHIC_LAYOUT.panelCard;
    return (
      <div style={{ ...itemStyle(it.x, it.y), opacity: alpha }}>
        <div style={{
          transform: `translateY(${(1 - Math.min(1, p)) * fs * 0.6}px) scale(${0.94 + 0.06 * Math.max(0, p)})`,
          boxSizing: "border-box", minWidth: fs * (L.minTextEm + L.padXEm * 2),
          background: sk.bg, borderRadius: fs * L.radiusEm, padding: `${fs * L.padYEm}px ${fs * L.padXEm}px`,
          border: sk.border > 0 ? `${Math.max(1.5, fs * sk.border * 2)}px solid ${sk.accent}` : undefined,
          boxShadow: sk.glow > 0 ? `0 0 ${fs * sk.glow * 2.2}px ${sk.accent}` : `0 ${fs * 0.12}px ${fs * 0.5}px rgba(0,0,0,0.35)`,
          fontFamily: ff, textAlign: "center",
        }}>
          <div style={{ fontWeight: fw(sk.weight), fontSize: fs * L.titleEm, color: sk.accent, lineHeight: 1.35 }}>{it.text}</div>
          {body.map((li, k) => {
            const lp = enterProgress(tRel, (it.start ?? 0) + 0.2 + k * 0.28);
            return (
              <div key={k} style={{ fontWeight: fw(Math.max(700, sk.weight - 100)), fontSize: fs, color: sk.fg, lineHeight: L.lineEm, opacity: Math.min(1, Math.max(0, lp) * 1.4) }}>{li}</div>
            );
          })}
        </div>
      </div>
    );
  }
  if (it.kind === "glyphReveal") {
    const sk = resolveInfographicSkin(it);
    const tr = tRel - (it.start ?? 0);
    const q = Math.max(0, Math.min(1, tr / 0.7));
    const fly = 1 - Math.pow(1 - q, 2.4);
    return (
      <>
        {sk.flash > 0 && tr >= 0 && tr < 0.12 ? (
          <div style={{ position: "absolute", inset: 0, background: "#fff", opacity: sk.flash * (1 - tr / 0.12) }} />
        ) : null}
        <div style={{ ...itemStyle(it.x, it.y), opacity: alpha }}>
          {q < 1 ? glyphSparks().map((s, k) => (
            <div key={k} style={{
              position: "absolute", left: "50%", top: "50%",
              width: Math.max(1, s.size * fs * (1 - q * 0.6) * 2), height: Math.max(1, s.size * fs * (1 - q * 0.6) * 2),
              marginLeft: Math.cos(s.angle) * s.dist * fs * fly, marginTop: Math.sin(s.angle) * s.dist * fs * fly,
              transform: "translate(-50%, -50%)", borderRadius: "50%", background: sk.accent, opacity: 1 - q,
            }} />
          )) : null}
          <div style={{
            transform: `scale(${Math.max(0.01, p)})`, fontFamily: ff, fontWeight: fw(sk.weight), fontSize: fs,
            color: sk.fg, lineHeight: 1, WebkitTextStroke: `${Math.max(2, fs * sk.border * 0.5)}px ${sk.accent}`,
            textShadow: sk.glow > 0 ? `0 0 ${fs * sk.glow}px ${sk.accent}` : undefined,
          }}>{it.text}</div>
        </div>
      </>
    );
  }
  if (it.kind === "bannerBurst") {
    // ★풀와이드 — x 는 무시하고 **실제 뷰포트 폭** 기준(캔버스 렌더러와 같은 W·같은 비율). 도형만 가로로
    //   터지고 글자는 안 찌그러진다.
    const sk = resolveInfographicSkin(it);
    const lines = [it.text, ...(it.sub ? [it.sub] : [])];
    // 📐 몸통·톱니·글로우는 배너 기하 SSOT 한 곳에서만 온다(#1885 — 캔버스 렌더러와 같은 함수).
    const banner = infographicBannerBurstMetrics(lines.length, sk.glow);
    const bh = fs * banner.bodyHeightEm;
    // 📐 폭 = 가운데 정렬 ui 안전 폭 상한(3-b) — 프레임 높이 = 640·S(스토리·가로 템플릿·배치 보드 공통 좌표계).
    const bw = viewportW(W, S) * infographicWideWidthFrac("bannerBurst", viewportW(W, S), 640 * S);
    const teeth = 22, tooth = fs * banner.toothEm;
    const pts: string[] = [];
    for (let k = 0; k < teeth; k++) {
      pts.push(`${(bw * (k + 0.5)) / teeth},0`, `${(bw * (k + 1)) / teeth},${tooth}`);
    }
    pts.unshift(`0,${tooth}`);
    for (let k = 0; k < teeth; k++) {
      pts.push(`${bw - (bw * k) / teeth},${tooth + bh}`, `${bw - (bw * (k + 0.5)) / teeth},${tooth * 2 + bh}`);
    }
    pts.push(`0,${tooth + bh}`);
    const textAlpha = Math.max(0, Math.min(1, (p - 0.25) / 0.5));
    return (
      <div style={{ position: "absolute", left: "50%", top: `${it.y * 100}%`, transform: "translate(-50%, -50%)", opacity: alpha }}>
        <div style={{ position: "relative", width: bw, height: bh + tooth * 2 }}>
          <div style={{ position: "absolute", inset: 0, transform: `scaleX(${Math.max(0.01, Math.min(1, p))})` }}>
            <svg width={bw} height={bh + tooth * 2} style={{ display: "block", filter: sk.glow > 0 ? `drop-shadow(0 0 ${fs * banner.glowBlurEm}px ${sk.accent})` : undefined }}>
              <polygon points={pts.join(" ")} fill={sk.accent} />
              <rect x={fs * 0.35} y={tooth + fs * 0.28} width={bw - fs * 0.7} height={bh - fs * 0.56} rx={fs * 0.25} fill={sk.bg} />
            </svg>
          </div>
          {/* 📐 글자만 안쪽 폭에 맞춰 줄인다(3-b · 캔버스와 같은 함수) · 줄은 접지 않는다(캔버스도 안 접는다). */}
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: ff, fontWeight: fw(sk.weight), fontSize: infographicWideTextPx("bannerBurst", fs, lines, bw), color: sk.fg, lineHeight: 1.25 * fs / infographicWideTextPx("bannerBurst", fs, lines, bw), opacity: textAlpha, whiteSpace: "nowrap" }}>
            {lines.map((li, k) => <div key={k}>{li}</div>)}
          </div>
        </div>
      </div>
    );
  }
  if (it.kind === "titleBand") {
    const sk = resolveInfographicSkin(it);
    const lines = it.text.split("\n").filter(Boolean).slice(0, 2);
    const badge = (it.sub ?? "").trim();
    // 📐 높이·뱃지 위치뿐 아니라 글로우·테두리도 밴드 기하 SSOT 한 곳에서만 온다(캔버스 렌더러와 같은 함수).
    const titleMetrics = infographicTitleBandMetrics(fs, lines.length, Boolean(badge), sk);
    const badgeFs = titleMetrics.badgeFontPx;
    const maxBandWidth = viewportW(W, S) * infographicWideWidthFrac("titleBand", viewportW(W, S), 640 * S);
    // 📐 카피 글자는 밴드 최대 폭에 맞춰 줄인다(3-b · 캔버스와 같은 함수) — 높이·패딩·뱃지는 원래 크기 기준.
    const textFs = infographicWideTextPx("titleBand", fs, lines, maxBandWidth);
    return (
      <div style={{ ...itemStyle(it.x, it.y), opacity: alpha }}>
        <div style={{
          transform: `translateY(${-(1 - Math.min(1, p)) * titleMetrics.heightPx}px)`,
          // 📐 폭 상한·최소 텍스트 폭도 공용 규칙(캔버스: min(W*0.94, max(글자폭, fs*8)+여백)).
          boxSizing: "border-box",
          height: titleMetrics.heightPx,
          maxWidth: maxBandWidth,
          minWidth: Math.min(maxBandWidth, fs * (INFOGRAPHIC_LAYOUT.titleBand.minTextEm + 2.4)),
          background: sk.bg, borderRadius: fs * 0.45, padding: `0 ${fs * 1.2}px`,
          // ⚠️ 여기 테두리는 `boxSizing: "border-box"` 라 **안쪽**에 그려진다(캔버스 stroke 는 절반이 바깥) —
          //   룩 차이는 종전 그대로 두고, 두께 숫자만 SSOT 에서 받는다.
          border: titleMetrics.borderWidthPx > 0 ? `${titleMetrics.borderWidthPx}px solid ${sk.accent}` : undefined,
          boxShadow: titleMetrics.glowBlurPx > 0 ? `0 0 ${titleMetrics.glowBlurPx}px ${sk.accent}` : undefined,
          fontFamily: ff, textAlign: "center", display: "flex", flexDirection: "column",
          position: "relative", alignItems: "center",
        }}>
          {/* 절대배치 본문은 intrinsic 폭에 기여하지 않는다. 같은 폰트/여백의 숨은 flow sizer로
              canvas의 max(copyW,badgeW,minText)+2.4em 폭 공식을 브라우저 레이아웃에도 그대로 준다.
              ⚠️ `opacity: 0` 필수 — @remotion/web-renderer(다운로드·스틸 경로)는 `visibility` 를 안 보고
              `display:none`·`opacity:0` 만 건너뛴다. 없으면 export PNG 에 문구가 한 번 더 찍힌다. */}
          <div
            data-testid="title-band-width-sizer"
            aria-hidden="true"
            style={{ display: "grid", height: 0, visibility: "hidden", opacity: 0, whiteSpace: "nowrap", pointerEvents: "none" }}
          >
            {badge ? <span style={{ gridArea: "1 / 1", justifySelf: "center", fontWeight: fw(sk.weight), fontSize: badgeFs, padding: `0 ${badgeFs * 0.6}px` }}>{badge}</span> : null}
            {lines.map((li, k) => <span key={k} style={{ gridArea: "1 / 1", justifySelf: "center", fontWeight: fw(sk.weight), fontSize: textFs }}>{li}</span>)}
          </div>
          {badge ? (
            <div style={{
              position: "absolute", left: "50%", top: titleMetrics.heightPx / 2 + (titleMetrics.badgeCenterY ?? 0),
              transform: "translate(-50%, -50%)", height: badgeFs * 1.44, display: "flex", alignItems: "center",
              background: sk.accent, color: sk.bg, fontWeight: fw(sk.weight), fontSize: badgeFs,
              lineHeight: 1, borderRadius: badgeFs, padding: `0 ${badgeFs * 0.6}px`,
            }}><span style={{ transform: `translateY(${titleMetrics.badgeTextOffsetY}px)` }}>{badge}</span></div>
          ) : null}
          {lines.map((li, k) => (
            <div key={k} style={{
              position: "absolute", left: "50%", top: titleMetrics.heightPx / 2 + titleMetrics.titleCenterYs[k],
              transform: "translate(-50%, -50%)", width: "100%", fontWeight: fw(sk.weight), whiteSpace: "nowrap",
              fontSize: textFs, color: sk.fg, lineHeight: INFOGRAPHIC_LAYOUT.titleBand.lineHeightEm,
            }}>{li}</div>
          ))}
        </div>
      </div>
    );
  }
  // arrow — 라벨 필 + 팁으로 draw-in 되는 화살선(SVG 풀프레임 오버레이·레이아웃 단위 360×640).
  const tip = it.tip ?? { x: Math.min(1, it.x + 0.12), y: Math.min(1, it.y + 0.14) };
  const sx = (it.x + (tip.x >= it.x ? 0.06 : -0.06)) * 360;
  const sy = (it.y + (tip.y >= it.y ? 0.035 : -0.035)) * 640;
  const ex = sx + (tip.x * 360 - sx) * p;
  const ey = sy + (tip.y * 640 - sy) * p;
  const ang = Math.atan2(ey - sy, ex - sx);
  const ah = Math.max(4, fs * 0.42 / Math.max(0.0001, S)); // SVG 는 레이아웃 단위라 S 역보정
  const lw = Math.max(1.6, (fs * 0.14) / Math.max(0.0001, S));
  return (
    <>
      <svg viewBox="0 0 360 640" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: alpha, pointerEvents: "none" }}>
        <line x1={sx} y1={sy} x2={ex} y2={ey} stroke={color} strokeWidth={lw} strokeLinecap="round" />
        {p > 0.85 ? (
          <polygon
            points={`${ex},${ey} ${ex - ah * Math.cos(ang - 0.45)},${ey - ah * Math.sin(ang - 0.45)} ${ex - ah * Math.cos(ang + 0.45)},${ey - ah * Math.sin(ang + 0.45)}`}
            fill={color}
          />
        ) : null}
      </svg>
      <div style={{ ...itemStyle(it.x, it.y), opacity: alpha }}>
        <div style={{ background: "rgba(17,17,17,0.85)", color: "#fff", fontFamily: ff, fontWeight: fw(900), fontSize: fs, padding: `${fs * 0.32}px ${fs * 0.5}px`, borderRadius: fs * 0.55, boxShadow: `0 ${fs * 0.1}px ${fs * 0.4}px rgba(0,0,0,0.35)` }}>
          {it.text}
        </div>
      </div>
    </>
  );
}

